import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/auth'

// Lazy-loaded components for code splitting
const HomeView = () => import('@/views/HomeView.vue')
const LoginView = () => import('@/views/LoginView.vue')
const RegisterView = () => import('@/views/RegisterView.vue')
const EmailVerificationView = () => import('@/views/EmailVerificationView.vue')
const EmailVerificationFormView = () => import('@/views/EmailVerificationFormView.vue')
const UsersView = () => import('@/views/UsersView.vue')
const ProfileView = () => import('@/views/ProfileView.vue')
const SetupView = () => import('@/views/SetupView.vue')
const AdminConfigView = () => import('@/views/AdminConfigView.vue')
const AdminAuthToolsView = () => import('@/views/AdminAuthToolsView.vue')
const AdminDatabaseView = () => import('@/views/AdminDatabaseView.vue')
const KeycloakCallbackView = () => import('@/views/KeycloakCallbackView.vue')
const NotFoundView = () => import('@/views/NotFoundView.vue')

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'home',
      component: HomeView,
    },
    {
      path: '/login',
      name: 'login',
      component: LoginView,
    },
    {
      path: '/register',
      name: 'register',
      component: RegisterView,
    },
    {
      path: '/verify-email/:token',
      name: 'email-verification',
      component: EmailVerificationView,
    },
    {
      path: '/verify-email-form',
      name: 'email-verification-form',
      component: EmailVerificationFormView,
    },
    {
      path: '/auth/callback',
      name: 'keycloak-callback',
      component: KeycloakCallbackView,
    },
    {
      path: '/verify-email-change/:token',
      name: 'email-change-verification',
      component: EmailVerificationView,
    },
    {
      path: '/users',
      name: 'users',
      component: UsersView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/config',
      name: 'admin-config',
      component: AdminConfigView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/auth-tools',
      name: 'admin-auth-tools',
      component: AdminAuthToolsView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/admin/database',
      name: 'admin-database',
      component: AdminDatabaseView,
      meta: { requiresAuth: true, requiresAdmin: true },
    },
    {
      path: '/profile',
      name: 'profile',
      component: ProfileView,
      meta: { requiresAuth: true },
    },
    {
      path: '/setup',
      name: 'setup',
      component: SetupView,
    },
    {
      path: '/404',
      name: 'not-found',
      component: NotFoundView,
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'catch-all',
      component: NotFoundView,
    },
  ],
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Check if auth is configured
  await authStore.checkAuthConfig()
  
  // Block access to setup route if setup is already complete
  if (to.name === 'setup' && !authStore.setupRequired) {
    // Setup is complete, show 404 page
    next({ name: 'not-found' })
    return
  }
  
  // Redirect to setup if required
  if (authStore.setupRequired && to.name !== 'setup') {
    next({ name: 'setup' })
    return
  }

  // If landing page is disabled and user is not authenticated, redirect to login
  if (to.name === 'home' && authStore.authEnabled && !authStore.isAuthenticated && !authStore.landingPageEnabled) {
    next({ name: 'login' })
    return
  }

  // If already authenticated, prevent access to login/register and redirect home
  if (authStore.authEnabled && authStore.isAuthenticated && (to.name === 'login' || to.name === 'register')) {
    next({ name: 'home' })
    return
  }

  // Prevent access to register when registration is disabled or auth is off
  if (to.name === 'register') {
    if (!authStore.authEnabled || !authStore.userRegistrationEnabled) {
      next({ name: 'home' })
      return
    }
  }
  
  // Check authentication for protected routes
  if (to.meta.requiresAuth && authStore.authEnabled && !authStore.isAuthenticated) {
    next({ name: 'login' })
    return
  }
  
  // Check admin role for admin-only routes
  if (to.meta.requiresAdmin && authStore.authEnabled && authStore.isAuthenticated && !authStore.isAdmin) {
    next({ name: 'home' })
    return
  }
  
  next()
})

export default router